import { useState } from "react";
import { ArrowRight, FilePenLine, SearchCheck, TriangleAlert, Wallet } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Eyebrow, Reveal, goTo } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const STEP_ICONS = [SearchCheck, FilePenLine, Wallet];

const fmt = (n) => "$" + Math.round(n).toLocaleString("en-US");

export default function Recovery() {
  const { t } = useLang();
  const [amount, setAmount] = useState([12500]);
  const gross = amount[0];
  const fee = gross * 0.2;
  const net = gross - fee;

  return (
    <section id="recovery" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <div className="relative overflow-hidden rounded-3xl border border-gold/15 bg-gradient-to-br from-jade/40 via-navy2 to-ink p-6 sm:p-10 lg:p-16">
          <img
            src="https://images.unsplash.com/photo-1562191326-0da0767cfffe?q=80&w=1600&auto=format&fit=crop"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.08]"
          />
          <div className="relative grid gap-14 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <Reveal>
                <Eyebrow className="mb-5">{t.recovery.eyebrow}</Eyebrow>
                <h2 className="font-display text-4xl font-semibold leading-[1.05] text-cream sm:text-5xl">
                  {t.recovery.titleA} <span className="italic text-gold-grad">{t.recovery.titleB}</span>
                </h2>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-cream/70">
                  {t.recovery.intro}
                </p>
              </Reveal>

              <Reveal delay={0.12}>
                <div
                  className="mt-8 flex items-start gap-4 rounded-2xl border border-gold/25 bg-ink/60 p-5"
                  data-testid="recovery-state-warning"
                >
                  <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                  <p className="text-sm leading-relaxed text-cream/80">
                    <span className="font-semibold text-gold">{t.recovery.warningLead}</span> {t.recovery.warning}
                  </p>
                </div>
              </Reveal>

              <div className="mt-10 space-y-4">
                {t.recovery.steps.map((step, i) => (
                  <Reveal key={step.title} delay={0.1 + i * 0.08}>
                    <div className="card-lift flex gap-5 rounded-2xl border border-cream/10 bg-ink/50 p-5" data-testid={`recovery-step-0${i + 1}`}>
                      <span className="font-mono text-sm text-gold/60">0{i + 1}</span>
                      {(() => { const Icon = STEP_ICONS[i]; return <Icon className="mt-0.5 h-5 w-5 shrink-0 text-gold" />; })()}
                      <div>
                        <h3 className="font-display text-xl font-semibold text-cream">{step.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-cream/60">{step.body}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            <Reveal delay={0.15}>
              <div className="sticky top-28 rounded-3xl border border-gold/25 bg-ink/80 p-7 shadow-[0_30px_80px_rgba(0,0,0,0.45)] backdrop-blur-md sm:p-9" data-testid="asset-recovery-calculator">
                <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">{t.recovery.calc.label}</p>
                <h3 className="mt-3 font-display text-3xl font-semibold text-cream">{t.recovery.calc.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/60">
                  {t.recovery.calc.description}
                </p>

                <div className="mt-9">
                  <div className="mb-4 flex items-baseline justify-between">
                    <label htmlFor="recovery-amount" className="font-mono text-[10px] uppercase tracking-[0.24em] text-cream/50">
                      {t.recovery.calc.estLabel}
                    </label>
                    <span className="font-display text-3xl font-semibold text-gold" data-testid="recovery-gross-amount">
                      {fmt(gross)}
                    </span>
                  </div>
                  <Slider
                    id="recovery-amount"
                    value={amount}
                    onValueChange={setAmount}
                    min={1000}
                    max={100000}
                    step={500}
                    data-testid="recovery-slider"
                  />
                  <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.18em] text-cream/40">
                    <span>$1,000</span>
                    <span>$100,000</span>
                  </div>
                </div>

                <dl className="mt-9 space-y-4 border-t border-cream/10 pt-7">
                  <div className="flex items-center justify-between text-sm">
                    <dt className="text-cream/60">{t.recovery.calc.grossLabel}</dt>
                    <dd className="font-medium text-cream">{fmt(gross)}</dd>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <dt className="text-cream/60">{t.recovery.calc.feeLabel}</dt>
                    <dd className="font-medium text-gold/90">&minus;{fmt(fee)}</dd>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-gold/25 bg-gold/10 px-4 py-4">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">{t.recovery.calc.netLabel}</dt>
                    <dd className="font-display text-3xl font-semibold text-gold" data-testid="recovery-net-amount">
                      {fmt(net)}
                    </dd>
                  </div>
                </dl>

                <a
                  href="#claim"
                  onClick={(e) => goTo(e, "#claim")}
                  data-testid="recovery-claim-cta"
                  className="btn-gold group mt-8 flex items-center justify-center gap-2.5 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-[0.14em]"
                >
                  {t.recovery.calc.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
